import { DeleteOutlined, DownloadOutlined, FilePdfOutlined, PlusOutlined, UploadOutlined } from "@ant-design/icons";
import { App, Button, Card, DatePicker, Form, Input, InputNumber, Select, Space, Upload } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import dayjs from "dayjs";
import { deleteQuoteDocument, generateQuotePdf, listQuoteDocuments, uploadQuoteDocument, type QuoteDocument } from "../../api/quoteApi";
import { PageHeaderActions } from "../../shared/components/PageHeaderActions";
import { PersistedTable } from "../../shared/components/PersistedTable";
import { formatDate } from "../../shared/utils/date";

type QuoteLine = { key: string; description: string; quantity: number; unit: string; rate: number; remarks?: string };

export function QuoteGeneratorPage() {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [quoteForm] = Form.useForm();
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [lines, setLines] = useState<QuoteLine[]>([{ key: crypto.randomUUID(), description: "", quantity: 1, unit: "Nos", rate: 0 }]);
  const query = useQuery({ queryKey: ["quote-documents"], queryFn: listQuoteDocuments });

  const columns: ColumnsType<QuoteDocument> = [
    { title: "Display Name", dataIndex: "displayName" },
    { title: "File", dataIndex: "originalFileName" },
    { title: "Type", dataIndex: "fileType", width: 100 },
    { title: "Uploaded", dataIndex: "uploadedAt", width: 160, render: formatDate },
    {
      title: "",
      width: 96,
      render: (_, row) => (
        <Space size={4}>
          <Button size="small" icon={<DownloadOutlined />} href={`/api/quote-documents/${row.id}/bytes`} target="_blank" />
          <Button size="small" danger icon={<DeleteOutlined />} onClick={async () => {
            await deleteQuoteDocument(row.id);
            message.success("Deleted");
            query.refetch();
          }} />
        </Space>
      )
    }
  ];

  return (
    <div>
      <PageHeaderActions title="Quote Generator" subtitle="Generate quote PDFs and maintain the document library." />
      <Card title="Generate Quote PDF">
        <Form form={quoteForm} layout="vertical" initialValues={{ quoteDate: dayjs() }} onFinish={async (values) => {
          setGenerating(true);
          try {
            const payload = {
              quoteId: 0,
              quoteNumber: values.quoteNumber,
              clientName: values.clientName,
              clientAddress: values.clientAddress,
              quoteDate: values.quoteDate?.format("YYYY-MM-DD"),
              validUntil: values.validUntil?.format("YYYY-MM-DD"),
              narration: values.narration,
              lineItems: lines.map((line) => ({
                description: line.description,
                quantity: line.quantity,
                unit: line.unit,
                rate: line.rate,
                amount: Number(line.quantity ?? 0) * Number(line.rate ?? 0),
                remarks: line.remarks
              })),
              attachments: (values.documentIds ?? []).map((id: number, index: number) => {
                const doc = query.data?.find((x) => x.id === id);
                return {
                  order: index + 1,
                  fileType: doc?.fileType ?? "pdf",
                  fileName: doc?.originalFileName ?? doc?.displayName ?? `Document ${id}`,
                  documentId: id
                };
              })
            };
            const blob = await generateQuotePdf(payload);
            const url = URL.createObjectURL(blob);
            window.open(url, "_blank", "noopener,noreferrer");
            setTimeout(() => URL.revokeObjectURL(url), 60_000);
          } catch (err: any) {
            message.error(err?.response?.data?.message ?? "Unable to generate quote");
          } finally {
            setGenerating(false);
          }
        }}>
          <div className="voucher-header-grid">
            <Form.Item name="quoteNumber" label="Quote No" rules={[{ required: true }]}><Input /></Form.Item>
            <Form.Item name="quoteDate" label="Quote Date" rules={[{ required: true }]}><DatePicker style={{ width: "100%" }} /></Form.Item>
            <Form.Item name="validUntil" label="Valid Until"><DatePicker style={{ width: "100%" }} /></Form.Item>
            <Form.Item name="clientName" label="Client Name" rules={[{ required: true }]}><Input /></Form.Item>
            <Form.Item name="clientAddress" label="Client Address"><Input /></Form.Item>
            <Form.Item name="documentIds" label="Append Documents">
              <Select mode="multiple" options={(query.data ?? []).map((doc) => ({ value: doc.id, label: doc.displayName }))} />
            </Form.Item>
            <Form.Item name="narration" label="Narration"><Input /></Form.Item>
          </div>
          <PersistedTable
            tableId="quote-generator-lines"
            rowKey="key"
            size="small"
            bordered
            pagination={false}
            dataSource={lines}
            columns={[
              { title: "Description", render: (_, row) => <Input value={row.description} onChange={(e) => setLines(lines.map((x) => x.key === row.key ? { ...x, description: e.target.value } : x))} /> },
              { title: "Qty", width: 110, render: (_, row) => <InputNumber value={row.quantity} min={0} onChange={(v) => setLines(lines.map((x) => x.key === row.key ? { ...x, quantity: Number(v ?? 0) } : x))} style={{ width: "100%" }} /> },
              { title: "Unit", width: 110, render: (_, row) => <Input value={row.unit} onChange={(e) => setLines(lines.map((x) => x.key === row.key ? { ...x, unit: e.target.value } : x))} /> },
              { title: "Rate", width: 130, render: (_, row) => <InputNumber value={row.rate} min={0} onChange={(v) => setLines(lines.map((x) => x.key === row.key ? { ...x, rate: Number(v ?? 0) } : x))} style={{ width: "100%" }} /> },
              { title: "Remarks", render: (_, row) => <Input value={row.remarks} onChange={(e) => setLines(lines.map((x) => x.key === row.key ? { ...x, remarks: e.target.value } : x))} /> },
              { title: "", width: 48, render: (_, row) => <Button danger size="small" icon={<DeleteOutlined />} onClick={() => setLines(lines.filter((x) => x.key !== row.key))} /> }
            ]}
          />
          <div className="voucher-line-footer">
            <Button icon={<PlusOutlined />} onClick={() => setLines([...lines, { key: crypto.randomUUID(), description: "", quantity: 1, unit: "Nos", rate: 0 }])}>Add Line</Button>
            <Button type="primary" icon={<FilePdfOutlined />} htmlType="submit" loading={generating}>Generate PDF</Button>
          </div>
        </Form>
      </Card>
      <Card title="Upload Quote Document">
        <Form form={form} layout="inline" onFinish={async (values) => {
          const file = values.file?.[0]?.originFileObj as File | undefined;
          if (!file) return message.warning("Select a PDF or image");
          setUploading(true);
          try {
            await uploadQuoteDocument(values.displayName, file);
            message.success("Uploaded");
            form.resetFields();
            query.refetch();
          } finally {
            setUploading(false);
          }
        }}>
          <Form.Item name="displayName" rules={[{ required: true }]}><Input placeholder="Display name" /></Form.Item>
          <Form.Item name="file" valuePropName="fileList" getValueFromEvent={(e) => e.fileList} rules={[{ required: true }]}>
            <Upload beforeUpload={() => false} maxCount={1} accept=".pdf,image/*"><Button icon={<UploadOutlined />}>Select File</Button></Upload>
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={uploading}>Upload</Button>
        </Form>
      </Card>
      <Card className="section-card" title="Quote Document Library">
        <PersistedTable tableId="quote-document-library" rowKey="id" size="small" loading={query.isLoading} dataSource={query.data ?? []} columns={columns} />
      </Card>
    </div>
  );
}
